import { useCallback, useEffect, useRef, useState } from 'react';
import { request } from '../../api/client';
import type { FileBrowserTarget } from './FileBrowserModal';
import { explorerApi } from './explorerApi';
import { beginFolderClick, folderHelperStatus, isMac, nativeFolderName, supportsNativeFolders, openHelperFolder, readFolderMode } from './folderMode';

export function useFolderOpen(userId: number, browser: (target: FileBrowserTarget) => void) {
  const pending = useRef<AbortController | null>(null);
  const [notice, setNotice] = useState('');
  const [choice, setChoice] = useState<FileBrowserTarget | null>(null);
  useEffect(() => () => pending.current?.abort(), [userId]);
  useEffect(() => {
    if (!supportsNativeFolders()) return;
    const check = () => { void folderHelperStatus(); };
    check(); const timer = setInterval(check, 5000);
    return () => clearInterval(timer);
  }, [userId]);
  const open = useCallback(async (assetId: number, title: string, paths: Record<string,string>, selectedPathName?: string) => {
    pending.current?.abort();
    const controller = new AbortController(); pending.current = controller;
    const pathNames = Object.keys(paths);
    const pathName = selectedPathName !== undefined && Object.hasOwn(paths, selectedPathName) ? selectedPathName : undefined;
    const target = { assetId, title, paths, ...(pathName !== undefined ? { pathName } : {}) }, mode = readFolderMode(userId);
    setNotice('');
    setChoice(null);
    if (mode === 'browser' || !supportsNativeFolders() || !pathNames.length) { browser(target); return; }
    if (pathNames.length > 1 && pathName === undefined) { setChoice(target); return; }
    const explicitNative = mode === 'windows' || mode === 'finder';
    if (explicitNative) setNotice('Проверяем доступ к папке…');
    try {
      // Launch while the actual click is active, before any network await.
      let nonce = beginFolderClick();
      const helper = nonce ? 'ready' : await folderHelperStatus();
      if (helper === 'ready' && !controller.signal.aborted) {
        nonce ??= beginFolderClick();
        if (!nonce) throw new Error('helper-not-ready');
        const location = await explorerApi.resolveAsset({ assetId, pathName: pathName ?? pathNames[0] });
        if (controller.signal.aborted) return;
        const { ticket } = await request<{ticket:string}>('/api/explorer/native-ticket', { method:'POST', body:location, signal:controller.signal });
        if (await openHelperFolder(ticket,controller.signal,nonce)) { if (!controller.signal.aborted) setNotice(''); return; }
      }
      if (!controller.signal.aborted) { browser(target); setNotice(explicitNative ? helper === 'update' ? 'Обновите помощник папок через меню выбора проводника.' : helper === 'unavailable' ? isMac() ? 'Установите и подключите помощник папок через меню «Finder».' : 'Установите или запустите помощник папок через меню выбора проводника. Если он уже работает, разрешите браузеру доступ к локальной сети.' : `Не удалось открыть эту папку в ${nativeFolderName()}. Проверьте помощник и доступ к студийной сети.` : ''); }
    } catch {
      if (!controller.signal.aborted) { browser(target); setNotice(explicitNative ? `Не удалось открыть папку в ${nativeFolderName()}. Открыт браузерный проводник.` : ''); }
    }
  }, [userId,browser]);
  const choose = (pathName: string) => {
    if (choice && Object.hasOwn(choice.paths, pathName)) void open(choice.assetId, choice.title, choice.paths, pathName);
  };
  const cancelChoice = () => setChoice(null);
  const dismiss = () => setNotice('');
  return { open, notice, dismiss, choice, choose, cancelChoice };
}
